"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type HookEvent = {
  id: string;
  ts: number;
  event: string;
  sessionId?: string;
  project?: string;
  tool?: string;
  summary?: string;
};

type SessionState = {
  id: string;
  project?: string;
  cwd?: string;
  status: "active" | "awaiting-approval" | "idle" | "done";
  lastEvent: string;
  lastTs: number;
  lastTool?: string;
  pendingApproval?: string;
  events: number;
};

type Project = {
  name: string;
  isGit: boolean;
  branch?: string;
  dirty?: boolean;
  lastCommit?: string;
};

function ago(ts: number, now: number) {
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

const STATUS_PILL: Record<SessionState["status"], { cls: string; label: string }> = {
  active: { cls: "green", label: "Active" },
  "awaiting-approval": { cls: "amber", label: "Needs approval" },
  idle: { cls: "gray", label: "Idle" },
  done: { cls: "gray", label: "Ended" },
};

export default function Dashboard() {
  const [events, setEvents] = useState<HookEvent[]>([]);
  const [sessions, setSessions] = useState<SessionState[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [connected, setConnected] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const sessionsRef = useRef<SessionState[]>([]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const [tokenSuffix] = useState(() => {
    if (typeof window === "undefined") return "";
    let t = localStorage.getItem("dnp_token");
    if (!t) {
      t = window.prompt("Enter DNP API token") || "";
      if (t) localStorage.setItem("dnp_token", t);
    }
    return t ? `?token=${encodeURIComponent(t)}` : "";
  });

  const loadSnapshot = async () => {
    try {
      const [s, p] = await Promise.all([
        fetch("/api/sessions" + tokenSuffix).then((r) => r.json()),
        fetch("/api/projects" + tokenSuffix).then((r) => r.json()),
      ]);
      setSessions(s.sessions || []);
      sessionsRef.current = s.sessions || [];
      setProjects(p.projects || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadSnapshot();
    const es = new EventSource("/api/stream" + tokenSuffix);
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.onmessage = (m) => {
      try {
        const data = JSON.parse(m.data);
        if (data.type === "init") {
          setEvents(data.events || []);
          if (data.sessions) {
            setSessions(data.sessions);
            sessionsRef.current = data.sessions;
          }
        } else if (data.type === "event") {
          setEvents((prev) => [data.event, ...prev].slice(0, 120));
          loadSnapshot();
        }
      } catch {
        /* ignore */
      }
    };
    const refresh = setInterval(loadSnapshot, 10000);
    return () => {
      es.close();
      clearInterval(refresh);
    };
  }, []);

  const active = useMemo(() => sessions.filter((s) => s.status === "active").length, [sessions]);
  const waiting = useMemo(
    () => sessions.filter((s) => s.status === "awaiting-approval").length,
    [sessions]
  );

  return (
    <div className="wrap">
      <div className="topbar">
        <div className="brand">
          DNP Claude Remote
          <small>Control plane · your Claude Code sessions, live</small>
        </div>
        <div className="spacer" />
        <div className="glass stat">
          <span className="n">{active}</span>
          <span className="l">Active</span>
        </div>
        <div className="glass stat">
          <span className="n">{waiting}</span>
          <span className="l">Awaiting</span>
        </div>
        <div className="glass stat">
          <span className="n">{sessions.length}</span>
          <span className="l">Sessions</span>
        </div>
        <div className="glass stat">
          <span className="live">
            <span className={`dot ${connected ? "green" : "gray"}`} />
            {connected ? "Live" : "Offline"}
          </span>
        </div>
      </div>

      <div className="grid">
        <div className="glass panel">
          <h2>Sessions</h2>
          <div className="body">
            {sessions.length === 0 && (
              <div className="empty">No sessions yet. Start one from the Claude app or terminal.</div>
            )}
            {sessions.map((s) => {
              const pill = STATUS_PILL[s.status];
              const dot =
                s.status === "active" ? "green" : s.status === "awaiting-approval" ? "amber" : "gray";
              return (
                <div className="row" key={s.id}>
                  <span className={`dot ${dot}`} />
                  <div className="main">
                    <div className="title">{s.project || s.cwd || s.id}</div>
                    <div className="sub">
                      {s.pendingApproval
                        ? s.pendingApproval
                        : s.lastTool
                        ? `${s.lastEvent} · ${s.lastTool}`
                        : s.lastEvent}
                    </div>
                  </div>
                  <span className={`pill ${pill.cls}`}>{pill.label}</span>
                  <span className="time">{ago(s.lastTs, now)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="glass panel">
          <h2>Live activity</h2>
          <div className="body">
            {events.length === 0 && <div className="empty">Waiting for events…</div>}
            {events.map((e) => (
              <div className="feed-item" key={e.id}>
                <span className="tag">{e.event}</span>
                <span className="txt">
                  {e.project ? <code>{e.project} </code> : null}
                  {e.summary || ""}
                </span>
                <span className="t">{ago(e.ts, now)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="projects">
        {projects.map((p) => (
          <div className="glass proj" key={p.name}>
            <div className="pn">
              <span className={`dot ${p.dirty ? "amber" : "green"}`} />
              {p.name}
            </div>
            <div className="pm">
              {p.isGit ? (
                <>
                  <span className="br">{p.branch || "—"}</span>
                  {p.dirty ? " · uncommitted changes" : " · clean"}
                  <br />
                  {p.lastCommit || ""}
                </>
              ) : (
                "Not a git repo"
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
